import { RootState } from '@src/state/types'
import {
  demoWorkspaceMatch,
  freeTrialTCMatch,
  papersMatch,
  plansMatch,
  rootMatch,
  wildcardMatch,
  workspaceMatch,
} from '@src/utils/routes'
import { ConnectedRouter } from 'connected-react-router'
import { History } from 'history'
import * as React from 'react'
import { connect } from 'react-redux'
import { Redirect, Route, RouteComponentProps, Switch, withRouter } from 'react-router'
import styled from 'styled-components'
import StyledErrorBoundary from '../ErrorBoundary/StyledErrorBoundary'
import Header from '../Header'
import CwcRollout from './CwcRollout'

import { getPaidMember } from '@src/state/auth/selectors'
import {
  cspUpsellInterstitialSelector,
  isDemoActiveSelector,
  isOptimizelyClientReadySelector,
} from '@src/state/featureFlags/selectors'
import Footer from '../Footer'
import ProtectedRoute from '../ProtectedRoute'
import Loading from './Loading'
import { useDisablePartialPlagCheck } from '@src/utils/hooks/usePartialPlagCheckFlag'

interface RouterProps {
  jwt: boolean
  history: History
  error: boolean
  clientReady: boolean
  demoActive: boolean
  isPaid: boolean
  showPlansPage?: boolean
}

const PapersRedirect = ({
  match,
  location,
}: Pick<RouteComponentProps, 'match' | 'location'>) => {
  const to: string = `${match.params[0] || '/'}${location.search}${location.hash}`
  return <Redirect to={to} />
}

type WrapperProps = RouteComponentProps
const RootWrapper = styled.div<WrapperProps>`
  position: ${props => {
    if (!!props.location.pathname.match(/\edit/)) {
      // editor page will have scrollbug if this is absolute
      // replicate by setting to 'absolute' and scrolling on left panel
      return 'fixed'
    }
    return 'absolute'
  }};
  top: 0;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
`

const RootWrapperWithRouter = withRouter(RootWrapper)

const FreeTrialTC = React.lazy(() => import('@src/pages/FreeTrialTC'))
const MyPapers = React.lazy(() => import('@src/pages/MyPapers'))
const ErrorPage = React.lazy(() => import('@src/pages/ErrorPage'))
const Workspace = React.lazy(() => import('@src/pages/Workspace'))
const DemoWorkspace = React.lazy(() => import('@src/pages/DemoWorkspace'))
const PlansPage = React.lazy(() => import('@src/pages/Plans'))

export const Router: React.FunctionComponent<RouterProps> = ({
  jwt,
  history,
  error,
  clientReady,
  demoActive,
  isPaid,
  showPlansPage,
}) => {
  useDisablePartialPlagCheck()
  return (
    <ConnectedRouter history={history}>
      <RootWrapperWithRouter>
        <Header />
        <CwcRollout />
        {(jwt || error) && clientReady ? (
          <React.Suspense fallback={<Loading />}>
            <StyledErrorBoundary>
              <Switch>
                <Route path={freeTrialTCMatch} exact={true} component={FreeTrialTC} />
                <Route path={papersMatch} component={PapersRedirect} />
                <ProtectedRoute
                  path={demoWorkspaceMatch}
                  component={DemoWorkspace}
                  authorized={demoActive && !isPaid}
                  redirectPath={'/'}
                />
                <Route path={workspaceMatch} component={Workspace} />
                <ProtectedRoute
                  path={plansMatch}
                  component={PlansPage}
                  authorized={!!showPlansPage}
                  redirectPath={'/'}
                />
                <Route path={rootMatch} exact={true} component={MyPapers} />
                <Route path={wildcardMatch} component={ErrorPage} />
              </Switch>
            </StyledErrorBoundary>
          </React.Suspense>
        ) : (
          <Loading />
        )}
        <Footer />
      </RootWrapperWithRouter>
    </ConnectedRouter>
  )
}

const mapStateToProps = (state: RootState) => ({
  jwt: !!state.auth.jwt,
  error: /error/.test(state.router.location.pathname),
  clientReady: isOptimizelyClientReadySelector(state),
  demoActive: isDemoActiveSelector(state),
  isPaid: getPaidMember(state),
  showPlansPage: cspUpsellInterstitialSelector(state),
})
export default connect(mapStateToProps)(Router)



// WEBPACK FOOTER //
// ./src/components/Router/index.tsx